/**
 * Popup exibido quando a API está fora do ar (health down, timeout, 5xx).
 *
 * Ao abrir, consulta o status real do servidor (EC2 + RDS) via proxy
 * `/api/scheduler`. A partir daí:
 *  - servidor DESLIGADO (economia noturna / fim de semana) → oferece o
 *    botão "Ligar servidor" e informa o tempo médio de subida;
 *  - servidor SUBINDO → pede para aguardar o tempo médio e tentar de novo;
 *  - servidor LIGADO mas inacessível → instabilidade real → tentar de novo
 *    + fallback de WhatsApp com o suporte.
 *
 * Disponível para qualquer usuário: o único "poder" é ligar recursos AWS,
 * sem acesso a dados.
 */

import { useCallback, useEffect, useState } from 'react'
import { Modal } from '@/design-system/molecules/Modal/Modal'
import { Button } from '@/design-system/atoms/Button'
import { Icon } from '@/design-system/atoms/Icon'
import {
  callScheduler,
  serverPhase,
  type ServerPhase,
  type SchedulerResponse,
} from '../../services/awsSchedulerService'
import { buildSupportMessage, buildWhatsAppUrl } from '../../constants/support'

/** Tempo médio observado nos logs para EC2 + RDS ficarem prontos. */
const AVERAGE_BOOT_MINUTES = 6

export interface SystemUnavailableModalProps {
  isOpen: boolean
  onClose: () => void
  onRetry: () => void
  userEmail?: string
  /**
   * Notifica o pai da fase diagnosticada do servidor. Usado na tela de
   * login para só alertar o Sentry quando o servidor está LIGADO mas
   * inacessível (incidente real), não quando está desligado de propósito.
   */
  onDiagnosis?: (phase: ServerPhase) => void
}

type CheckState = 'checking' | 'ready' | 'error'

export function SystemUnavailableModal({
  isOpen,
  onClose,
  onRetry,
  userEmail,
  onDiagnosis,
}: SystemUnavailableModalProps) {
  const [checkState, setCheckState] = useState<CheckState>('checking')
  const [status, setStatus] = useState<SchedulerResponse | null>(null)
  const [waking, setWaking] = useState(false)
  const [wakeRequested, setWakeRequested] = useState(false)
  const [wakeError, setWakeError] = useState<string | null>(null)

  const refreshStatus = useCallback(async () => {
    setCheckState('checking')
    try {
      const data = await callScheduler('status')
      setStatus(data)
      setCheckState('ready')
      onDiagnosis?.(serverPhase(data))
    } catch {
      setStatus(null)
      setCheckState('error')
    }
  }, [onDiagnosis])

  // Consulta o status toda vez que o popup abre (reseta o fluxo de wake).
  useEffect(() => {
    if (!isOpen) return
    setWakeRequested(false)
    setWakeError(null)
    void refreshStatus()
  }, [isOpen, refreshStatus])

  const handleWake = useCallback(async () => {
    setWaking(true)
    setWakeError(null)
    try {
      await callScheduler('start')
      setWakeRequested(true)
      await refreshStatus()
    } catch (err) {
      setWakeError(
        err instanceof Error ? err.message : 'Falha ao ligar o servidor.'
      )
    } finally {
      setWaking(false)
    }
  }, [refreshStatus])

  const phase = serverPhase(status)
  const whatsappUrl = buildWhatsAppUrl(buildSupportMessage({ userEmail }))

  // "Subindo" cobre tanto o start que acabei de disparar quanto um start
  // já em curso (auto-scheduler ou outro usuário).
  const isWaking = wakeRequested || phase === 'starting' || phase === 'mixed'

  const title = (() => {
    if (checkState === 'checking') return 'Verificando o servidor...'
    if (checkState === 'error') return 'Sistema indisponível'
    if (isWaking) return 'Servidor ligando'
    if (phase === 'stopped') return 'Servidor desligado'
    return 'Sistema instável'
  })()

  const titleIcon = (() => {
    if (isWaking) return 'clock'
    if (phase === 'stopped' && checkState === 'ready') return 'plug'
    return 'alert-triangle'
  })()

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={
        <span className="flex items-center gap-2">
          <Icon name={titleIcon} size={20} className="text-amber-500" />
          {title}
        </span>
      }
      size="md"
      footer={
        <div className="flex w-full flex-col-reverse gap-2 sm:flex-row sm:justify-end">
          <Button variant="outline" size="sm" onClick={onClose}>
            Fechar
          </Button>
          {checkState === 'ready' && phase === 'stopped' && !wakeRequested ? (
            <Button
              variant="primary"
              size="sm"
              onClick={() => void handleWake()}
              disabled={waking}
            >
              <Icon name="plug" size={16} className="mr-2" />
              {waking ? 'Ligando...' : 'Ligar servidor'}
            </Button>
          ) : (
            <Button
              variant="primary"
              size="sm"
              onClick={onRetry}
              disabled={waking}
            >
              <Icon name="refresh" size={16} className="mr-2" />
              Tentar entrar
            </Button>
          )}
        </div>
      }
    >
      <div className="space-y-3 text-sm text-gray-700 dark:text-gray-300">
        {checkState === 'checking' && (
          <p>Verificando o estado do servidor, um instante...</p>
        )}

        {checkState === 'error' && (
          <>
            <p>
              Não conseguimos falar com o sistema agora. Pode ser uma falha
              pontual de rede.
            </p>
            <p>Tente novamente em alguns instantes.</p>
            <SupportFallback whatsappUrl={whatsappUrl} />
          </>
        )}

        {checkState === 'ready' && isWaking && (
          <>
            <p>
              O servidor está ligando. Ele leva cerca de{' '}
              <span className="font-medium">
                {AVERAGE_BOOT_MINUTES} minutos
              </span>{' '}
              para ficar pronto (banco de dados + aplicação).
            </p>
            <p>
              Aguarde esse tempo e clique em{' '}
              <span className="font-medium">Tentar entrar</span> — não precisa
              recarregar a página.
            </p>
          </>
        )}

        {checkState === 'ready' && !isWaking && phase === 'stopped' && (
          <>
            <p>
              O servidor está desligado — fora do horário de funcionamento ele
              fica em espera para economizar recursos.
            </p>
            <p>
              Clique em <span className="font-medium">Ligar servidor</span>{' '}
              abaixo. A subida completa leva cerca de{' '}
              <span className="font-medium">
                {AVERAGE_BOOT_MINUTES} minutos
              </span>{' '}
              (banco de dados + aplicação); depois disso, é só entrar.
            </p>
            {wakeError && (
              <p className="text-red-600 dark:text-red-400">{wakeError}</p>
            )}
          </>
        )}

        {checkState === 'ready' && !isWaking && phase === 'running' && (
          <>
            <p>
              O servidor está ligado, mas não conseguimos concluir o acesso
              agora. Pode ser uma instabilidade momentânea.
            </p>
            <p>Tente de novo em alguns instantes.</p>
            <SupportFallback whatsappUrl={whatsappUrl} />
          </>
        )}
      </div>
    </Modal>
  )
}

function SupportFallback({ whatsappUrl }: { whatsappUrl: string }) {
  return (
    <p className="pt-1 text-xs text-gray-500 dark:text-gray-400">
      Ainda com problema?{' '}
      <a
        href={whatsappUrl}
        target="_blank"
        rel="noopener noreferrer"
        className="inline-flex items-center gap-1 font-medium text-brand-500 hover:text-brand-600 dark:text-brand-400"
      >
        <Icon name="message-square-text" size={14} />
        Falar com o suporte
      </a>
    </p>
  )
}
